// Singleton store for the fail2ban / security domain. Mirrors the
// shape of actions.svelte.ts: one WebSocket subscription, an initial
// /summary fetch on boot, and reactive state that any page can
// read via the exported getters. Toast is fired on every ban event
// so the user sees pushes regardless of which page they're on
// (principles.md §1: Push, nicht Pull).
//
// Summary, aktive Bans und das WS-Abo "fail2ban" laufen seit dem Boot
// (Dashboard-Karte, Toast, alerts-store). Die Ereignis- und Fund-Listen
// (events + found) liest nur die Seite Sicherheit: sie werden nur geholt,
// solange sie offen ist (beobachteSicherheit, lib/bei-bedarf.ts, Takt 30 s).

import { untrack } from "svelte";
import { SvelteMap } from "svelte/reactivity";
import { beiBedarf } from "./bei-bedarf";
import { registerRevalidator } from "./revalidate.svelte";

import { loadPersisted, persist } from "./persistent-summary.svelte";
import { toast } from "./toast.svelte";
import type {
  Fail2banWSEvent,
  SecurityBan,
  SecurityEvent,
  SecuritySummary,
} from "./types";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export interface SecurityFound {
  id: number;
  ts: string;
  host_id: string;
  jail: string;
  ip: string;
}

const KEY_SUMMARY = "security";
const KEY_ACTIVE = "security-active";
const KEY_EVENTS = "security-events";
const KEY_FOUND = "security-found";

// Reactive state. SvelteMap is reactive on insert/delete; the
// summary/lastEvent slots use a SvelteMap-of-one as a tiny hack so
// any consumer's $derived re-runs when they change.
const _summary = new SvelteMap<string, SecuritySummary>();
const _activeBans = new SvelteMap<string, SecurityBan>(); // key: jail|ip
const _events = new SvelteMap<string, SecurityEvent[]>();
const _found = new SvelteMap<string, SecurityFound[]>();

let _unsubscribe: (() => void) | undefined;
let _started = false;

// banKey is the SvelteMap key used for active bans. (jail, ip) is the
// natural composite per the schema.
function banKey(jail: string, ip: string): string {
  return `${jail}|${ip}`;
}

async function refreshSummary(): Promise<void> {
  try {
    const r = await fetch("/v2-api/security/summary", { credentials: "include" });
    if (!r.ok) return;
    const s = (await r.json()) as SecuritySummary;
    s.spark = await fetchSpark();
    _summary.set("current", s);
    persist(KEY_SUMMARY, s);
  } catch {
    // ignore — WS will fill in on next event
  }
}

interface TimeseriesPoint {
  bucket: string;
  value: number;
}

// Trailing 24 h Bans-pro-Bucket als Spark-Line. Identisches Muster
// wie tasks-summary; eigener Fetch damit /summary klein bleibt.
async function fetchSpark(): Promise<{ values: number[] } | undefined> {
  try {
    const r = await fetch(
      "/v2-api/events/timeseries?domain=fail2ban&type=ban&hours=24",
      { credentials: "include" },
    );
    if (!r.ok) return undefined;
    const points = (await r.json()) as TimeseriesPoint[];
    if (points.length === 0) return undefined;
    return { values: points.map((p) => p.value) };
  } catch {
    return undefined;
  }
}

async function refreshEvents(): Promise<void> {
  try {
    const r = await fetch("/v2-api/security/events?limit=200", {
      credentials: "include",
    });
    if (!r.ok) return;
    const list = (await r.json()) as SecurityEvent[];
    _events.set("current", list);
    persist(KEY_EVENTS, list);
  } catch {
    // ignore -- naechster Tick versucht's wieder
  }
}

async function refreshFound(): Promise<void> {
  try {
    const r = await fetch("/v2-api/security/found", { credentials: "include" });
    if (!r.ok) return;
    const list = (await r.json()) as SecurityFound[];
    _found.set("current", list);
    persist(KEY_FOUND, list);
  } catch {
    // ignore
  }
}

async function refreshActiveBans(): Promise<void> {
  try {
    const r = await fetch("/v2-api/security/active", { credentials: "include" });
    if (!r.ok) return;
    const arr = (await r.json()) as SecurityBan[];
    _activeBans.clear();
    for (const b of arr) {
      _activeBans.set(banKey(b.jail, b.ip), b);
    }
    persist(KEY_ACTIVE, arr);
  } catch {
    // ignore
  }
}

function applyEvent(ev: Fail2banWSEvent): void {
  const key = banKey(ev.jail, ev.ip);
  if (ev.type === "ban") {
    _activeBans.set(key, {
      // ID kommt erst beim naechsten loadActive() rein -- der WS-
      // Push hat sie nicht. Dismiss-Button ist disabled bis der
      // refresh durch ist.
      id: 0,
      ts: ev.ts,
      host_id: ev.host_id,
      jail: ev.jail,
      ip: ev.ip,
    });
    // Awareness toast — only on bans (unban is a relief, no
    // notification needed). Quick-and-clear: jail + IP.
    toast.error(`fail2ban: IP ${ev.ip} in ${ev.jail} gebannt`, 8000);
  } else if (ev.type === "unban") {
    _activeBans.delete(key);
  }
  // Re-fetch the summary aggregate so the card status flips
  // promptly. The summary endpoint is cheap (3 queries) and we only
  // hit it once per event. Plus events-Liste, weil ein neuer Ban als
  // Event auftaucht und /security die Historie zeigt.
  void refreshSummary();
  if (_listen.aktiv()) void refreshEvents();
}

// Ereignisse + Funde: ein Beobachter für beide (WS deckt nur Bans/Unbans ab,
// darum der Takt als Failsafe).
const _listen = beiBedarf({
  hole: () => Promise.all([refreshEvents(), refreshFound()]).then(() => {}),
  takt: 30_000,
});

export function startSecurityStream(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(() => {
    void refreshSummary();
    void refreshActiveBans();
    if (_listen.aktiv()) {
      void refreshEvents();
      void refreshFound();
    }
  });
  // SWR: gecachten Summary + Active-Bans sofort einspielen, damit
  // Dashboard-Card und /security beim Reload nicht erst auf den
  // Initial-Fetch warten muessen.
  const cachedSum = loadPersisted<SecuritySummary>(KEY_SUMMARY);
  if (cachedSum) _summary.set("current", cachedSum);
  const cachedActive = loadPersisted<SecurityBan[]>(KEY_ACTIVE);
  if (cachedActive) {
    for (const b of cachedActive) _activeBans.set(banKey(b.jail, b.ip), b);
  }
  // events + found: nur der SWR-Cache; geholt wird mit einem Beobachter.
  const cachedEvents = loadPersisted<SecurityEvent[]>(KEY_EVENTS);
  if (cachedEvents) _events.set("current", cachedEvents);
  const cachedFound = loadPersisted<SecurityFound[]>(KEY_FOUND);
  if (cachedFound) _found.set("current", cachedFound);
  void Promise.all([refreshSummary(), refreshActiveBans()]).then(() => {
    if (!_started) return;
    _unsubscribe = wsSubscribe("fail2ban", (data) => {
      const ev = data as Fail2banWSEvent | undefined;
      if (!ev) return;
      applyEvent(ev);
    });
  });
}

// Anmelden, solange die Seite Sicherheit offen ist (Ereignisse + Funde); der
// Rückgabewert meldet wieder ab. In Svelte:
// $effect(() => { if (zuSehen()) return beobachteSicherheit(); }).
export function beobachteSicherheit(): () => void {
  return untrack(() => {
    startSecurityStream();
    return _listen.beobachte();
  });
}

export function stopSecurityStream(): void {
  _started = false;
  if (_unsubscribe) {
    _unsubscribe();
    _unsubscribe = undefined;
  }
  _listen.beende();
  _summary.clear();
  _activeBans.clear();
  _events.clear();
  _found.clear();
}

// refreshSecurityNow stoesst einen sofortigen Refetch an (Summary +
// aktive Bans + Events) -- genutzt nach manuellem Ban/Unban, damit die
// UI nicht auf den naechsten Poll/WS-Frame warten muss.
export function refreshSecurityNow(): void {
  void refreshSummary();
  void refreshActiveBans();
  void refreshEvents();
}

export function getSummary(): SecuritySummary | undefined {
  return _summary.get("current");
}

export function getActiveBans(): SecurityBan[] {
  return Array.from(_activeBans.values()).sort((a, b) =>
    b.ts.localeCompare(a.ts),
  );
}

export function getSecurityEvents(): SecurityEvent[] | undefined {
  return _events.get("current");
}

export function getSecurityFound(): SecurityFound[] | undefined {
  return _found.get("current");
}

// Lokaler Drop nach Ack -- vermeidet Refetch.
export function dropFound(id: number): void {
  const cur = _found.get("current");
  if (!cur) return;
  const next = cur.filter((f) => f.id !== id);
  _found.set("current", next);
  persist(KEY_FOUND, next);
}
